<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { ECOMMERCE_ITEM_TAG, type EcommerceItem } from '@/types/shared/player-config'
import ShopCard from '@/components/ShopCard.vue'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import type { EcommercePrizeCardItem } from '@/types/shop'
import BoostCard from '@/components/BoostCard.vue'
import { computed, ref } from 'vue'
import { useShop } from '@/composables/game/shop'
import { formatUnshortenedNumber } from '@/helpers/utils'
import PaymentButtons from '@/components/PaymentButtons.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import type { BadgeVariant } from '@/components/periodic/DefaultBadge.vue'
import DefaultBadge from '@/components/periodic/DefaultBadge.vue'

const props = defineProps<{
	show: boolean
	item: EcommerceItem | EcommercePrizeCardItem
	bgFadeClass?: string
	wrapperClass?: string
}>()
const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'onPurchase'): void
}>()

const { getStarPrice } = useEcommercePrice()
const { isDefaultBOGO, isDefaultCashback, isCashback, isBOGO, saleValue, isDefaultBOGX, isBOGX } = useShop()

const starPrice = getStarPrice(props.item)
const isLoading = ref(false)

const saleVariant = computed<BadgeVariant>(() => {
  if (isDefaultBOGO.value) return 'BOGO'
  if (isDefaultBOGX.value) return 'BOGX'
  if (isDefaultCashback.value) return 'cashback'
  return 'discount' // fallback
})

function handlePurchase(paid: boolean) {
  if (paid) {
    emit('onPurchase')
  }
}

</script>

<template>
  <TheBottomSheet
    :show="show"
    :bg-fade-class="bgFadeClass"
    :wrapper-class="wrapperClass"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center gap-y-12 relative">
      <StarsBalancePill
        v-if="!item.config.disableInGameStar"
        class="absolute left-0 -top-6"
      />
      <div class="flex flex-col items-center gap-y-6">
        <div :class="{'w-[88px] h-[116px] relative': !$slots.itemIcon}">
          <DefaultBadge
            v-if="item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT) && (isCashback || isBOGO || isBOGX)"
            :variant="saleVariant"
            class="absolute -left-4 -top-5 w-[55px] h-auto z-[3] -rotate-[10deg]"
            :class="(isDefaultBOGO || isDefaultBOGX) && 'overflow-hidden'"
          />
          <slot
            v-if="$slots.itemIcon"
            name="itemIcon"
          />
          <BoostCard
            v-else-if="('timeImage' in item)"
            :item="item as EcommercePrizeCardItem"
            only-visual
          />
          <ShopCard
            v-else
            :item="item"
            :is-buying="false"
            :show-badge="!isCashback && !isBOGO && !isBOGX"
            only-visual
          />
        </div>
        <div class="flex flex-col items-center w-full">
          <div class="flex text-center max-w-[360px] text-2xl font-bold -tracking-[0.48px] text-light-white">
            <span v-if="isBOGO">2x&nbsp;</span>
            <span>
              {{ item.name }} = ${{ starPrice?.usdAmount }}
            </span>
          </div>
          <div
            v-if="isCashback && saleValue"
            class="w-full flex items-center text-center justify-center"
          >
            <span class="text-base font-medium text-white/60 whitespace-pre-line text-center">
              <T
                key-name="shop.details.cashback"
                :default-value="`Get {count}% back on your\n Stars Balance`"
                :params="{count: saleValue * 100}"
              /> = <ThePremium
                :with-tooltip="false"
                class="size-4 inline"
                image-class="inline"
              /> {{ formatUnshortenedNumber(starPrice.amount * saleValue) }}

            </span>
          </div>
        </div>
      </div>
      <PaymentButtons
        :item="item"
        @purchase="({ paid }) => handlePurchase(paid)"
      />
    </div>
  </TheBottomSheet>
</template>
